import { forwardRef } from "react";

import { Box, BoxProps, Collapse, Stack, useTheme } from "@mui/material";

import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeSx } from "@/helpers/mergeSx";
import { StripeRadioEmpty, StripeRadioSelected } from "@/icons/external";

export type BaseFormPaymentMethodProps = BoxProps & {
  icon: React.ReactNode;
  label: string;
  firstItem?: boolean;
  lastItem?: boolean;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
};

export const BaseFormPaymentMethod = forwardRef<HTMLDivElement, BaseFormPaymentMethodProps>(
  ({ icon, label, children, checked = false, onChange, firstItem, lastItem, ...rest }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.paymentMethod) {
      return <InvalidThemeErrorMessage />;
    }

    const styles = mergeSx(
      {
        ...theme.typography.body2,
        color: theme.palette.formsComponents.paymentMethod.textColor,
        backgroundColor: theme.palette.formsComponents.paymentMethod.backgroundColor,
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: theme.palette.formsComponents.paymentMethod.borderColor,
        outlineColor: "transparent",
        transition: "outline 100ms ease-out, background-color 150ms ease-out",
        ...(firstItem && {
          borderStartStartRadius: theme.spacing(1),
          borderStartEndRadius: theme.spacing(1),
        }),
        ...(!firstItem && {
          marginTop: "-1px",
        }),
        ...(lastItem && {
          borderEndStartRadius: theme.spacing(1),
          borderEndEndRadius: theme.spacing(1),
        }),
      },
      rest.sx
    );

    return (
      <Box ref={ref} {...rest} sx={styles}>
        <Stack
          direction="row"
          alignItems="center"
          padding={2}
          sx={{ cursor: "pointer" }}
          onClick={() => onChange?.(true)}
        >
          <FormIcon width="1.2em" height="1.2em" size="other" sx={{ marginInlineEnd: 1.5 }}>
            {checked ? <StripeRadioSelected /> : <StripeRadioEmpty />}
          </FormIcon>
          <FormIcon
            width="1.2em"
            height="1.2em"
            color={theme.palette.formsComponents.paymentMethod.iconColor}
            size="other"
            sx={{ marginInlineEnd: "25.2px" }}
          >
            {icon}
          </FormIcon>
          <FormTypography variant="subtitle2" sx={{ flex: "1 1 auto" }}>
            {label}
          </FormTypography>
        </Stack>
        {children && (
          <Collapse in={checked} collapsedSize={0}>
            <Box padding={theme.spacing(0, 1.5, 2, 1.5)}>{children}</Box>
          </Collapse>
        )}
      </Box>
    );
  }
);

BaseFormPaymentMethod.displayName = "BaseFormPaymentMethod";
